/**
 * INBOX (INBOX.md) — pure: everything that needs a person, sorted into three segments with one actionable count.
 *
 *   action    — invitations to you · an invite link you opened · approvals delegated to you · @-mentions of you
 *               (everyone, their own)
 *   alerts    — tripped release alarms · tripped room alarms                                    (owners/admins)
 *   overwatch — escalated approvals across the workspace                                        (owners/admins)
 *
 * Each category is one source block below — adding one (billing warnings, @mentions, blocking Gaps) is one block.
 */

export const SEGMENTS = [
  { id: 'action', label: 'Action needed' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'overwatch', label: 'Overwatch' },
];

const tripped = (r) => String((r && r.Status) || '').toLowerCase() === 'tripped';

/**
 * @param {object} s
 * @param {Array}  s.invites      pendingInvites from sign-in ([{tenantId, name, token, role}])
 * @param {string} s.inviteLink   an invite token the person opened (sessionStorage) — '' when none
 * @param {Array}  s.alarms       release alarms ([{Name, Status, LastError}])
 * @param {Array}  s.roomAlarms   room alarms ([{Name, Status, AlarmNetworkId, Anchor, LastError}])
 * @param {Array}  s.approvals    tenant approvals ([{taskId, networkId, poolId, status, delegatedTo}])
 * @param {string} s.me           the person's sub (approvals delegated to them)
 * @param {boolean} s.isAdmin     owners/admins see alerts + overwatch
 * @param {Array}  s.added        workspaces an admin ADDED you to ([{tenantId, name, role}]) — FYI, never counted
 * @param {Array}  s.rooms        the person's rooms (GET /my/agentic-networks) — lifecycle items (LIFECYCLE.md §5)
 * @param {boolean} s.isOwner     workspace OWNER — also hears about retired / archived rooms (Overwatch)
 * @param {Array}  s.mentions     unread @-mentions of you ([{networkId, poolId, roomName, fromName, excerpt, at}]) — MENTIONS.md
 * @returns {{segments: {action: Array, alerts: Array, overwatch: Array}, count: number, severity: 'none'|'warn'|'critical'}}
 */
export function buildInbox({ invites = [], inviteLink = '', alarms = [], roomAlarms = [], approvals = [], me = '',
  isAdmin = false, added = [], mentions = [], modelNews = [], rooms = [], isOwner = false, promotions = [],
  expertUpdates = [], subscriptionRequests = [], subscriptionDecisions = [], now = Date.now() } = {}) {
  const action = [];
  const alerts = [];
  const overwatch = [];

  // ── invitations ──
  const seen = new Set();
  for (const inv of invites || []) {
    if (!inv || !inv.token || seen.has(inv.token)) continue;
    seen.add(inv.token);
    action.push({ id: `invite:${inv.token}`, kind: 'invite', title: `Invitation to ${inv.name || inv.tenantId || 'a workspace'}`,
      detail: `as ${String(inv.role || 'USER').toLowerCase()}`, to: `/join?token=${encodeURIComponent(inv.token)}`,
      cta: 'Join' });
  }
  if (inviteLink && !seen.has(inviteLink)) {
    action.push({ id: `invite:${inviteLink}`, kind: 'invite', title: 'An invitation link you opened',
      detail: 'finish joining that workspace', to: `/join?token=${encodeURIComponent(inviteLink)}`, cta: 'Continue' });
  }

  // ── added to a workspace (no accept step — FYI, not counted) ──
  for (const j of added || []) {
    if (!j || !j.tenantId) continue;
    action.push({ id: `added:${j.tenantId}`, kind: 'added', fyi: true,
      title: `You were added to ${j.name || j.tenantId}`, detail: `as ${String(j.role || 'USER').toLowerCase()}`,
      tenantId: j.tenantId, cta: 'Got it' });
  }

  // ── @-mentions of you (MENTIONS.md) — one item per ROOM (newest first), opening it reads them ──
  const byRoom = new Map();
  for (const m of mentions || []) {
    if (!m || !m.networkId) continue;
    const k = `${m.networkId}:${m.poolId || 'default'}`;
    const cur = byRoom.get(k);
    if (cur) cur.n += 1; else byRoom.set(k, { n: 1, m });
  }
  for (const [k, { n, m }] of byRoom) {
    action.push({ id: `mention:${k}`, kind: 'mention',
      title: `${m.fromName || 'Someone'} mentioned you in ${m.roomName || m.poolId || 'a room'}${n > 1 ? ` (+${n - 1})` : ''}`,
      detail: m.excerpt || '', cta: 'Open room',
      to: `/agentic?networkId=${encodeURIComponent(m.networkId)}&poolId=${encodeURIComponent(m.poolId || 'default')}` });
  }

  // ── NEW MODEL AVAILABLE (MODEL-CATALOG.md §6) — owners/admins only: a Claude release the catalog added in the last 14
  //    days that this workspace doesn't use yet. Choosing it is THEIR call (the LLM tab), so it's an ACTION item. ──
  if (isAdmin) {
    for (const m of modelNews || []) {
      if (!m || !m.id) continue;
      const tiers = (m.tiers || []).map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(' / ');
      action.push({ id: `model:${m.id}`, kind: 'model', title: `New model available: ${m.label || m.id}`,
        detail: `${tiers ? `for ${tiers}` : ''}${m.priceEstimated ? ' · price estimated until AWS lists it' : ''}`,
        to: '/workspace/settings?tab=llm', cta: 'Review' });
    }
  }

  // ── PRODUCTION RELEASE TO APPROVE (EXPERT-ROOMS.md §15, four eyes) — someone ELSE asked to put an expert's release
  //    in Production; never shown to the person who asked (they can't approve their own). ──
  // ── A COMPANY EXPERT YOU SUBSCRIBE TO RELEASED A NEW VERSION (EXPERT-ROOMS §18) — FYI; the key carries the version,
  //    so every release is a new, unread item. ──
  for (const x of expertUpdates || []) {
    if (!x || !x.expertNetworkId) continue;
    alerts.push({ id: `xupdate:${x.expertTenantId || ''}:${x.expertNetworkId}:${x.label || x.version || x.at}`, kind: 'expert-update',
      title: `${x.name || x.expertNetworkId} ${x.label ? `${x.label} ` : ''}is in production`,
      detail: x.summary || 'A new release of an expert your rooms use.', to: '/registry?tab=experts', cta: 'Read it' });
  }

  // ── A WORKSPACE ASKS TO USE ONE OF YOUR EXPERTS (EXPERT-ROOMS §14a) — decided RIGHT HERE (Approve / Decline);
  //    the network's Subscriptions tab holds the full list. ──
  for (const r of subscriptionRequests || []) {
    if (!r || !r.expertNetworkId || !r.tenantId) continue;
    action.push({ id: `xsub:${r.expertNetworkId}:${r.tenantId}`, kind: 'subscription-request',
      title: `${r.tenantName || 'A workspace'} wants to use ${r.expertName || r.expertNetworkId}`,
      detail: 'Approve to let its rooms plug the expert in and ask it — only their questions come to you.',
      to: `/network/${encodeURIComponent(r.expertNetworkId)}?tab=settings&sub=subscriptions`, cta: 'Review',
      inline: [{ id: 'approve', label: 'Approve', primary: true }, { id: 'decline', label: 'Decline' }],
      payload: { expertNetworkId: r.expertNetworkId, tenantId: r.tenantId } });
  }
  // ── YOUR WORKSPACE'S ACCESS TO AN EXPERT WAS DECIDED (§14a) — FYI for 30 days; the key carries the decision time ──
  for (const x of subscriptionDecisions || []) {
    if (!x) continue;
    // §20c — an expert your workspace uses is RETIRING: notice until the date (the key carries it — a new date, a new item)
    if (x.retireAt && x.status !== 'revoked') {
      alerts.push({ id: `xretire:${x.expertTenantId || ''}:${x.expertNetworkId}:${x.retireAt}`, kind: 'expert-retiring',
        title: `${x.name || x.expertNetworkId} retires on ${String(x.retireAt).slice(0, 10)}`,
        detail: x.retireNote || 'After that date your rooms no longer reach it — plan for what replaces it.',
        to: '/registry?tab=experts', cta: 'See experts' });
    }
    if (!x.decidedAt || !['active', 'revoked'].includes(x.status)) continue;
    if (now - Date.parse(x.decidedAt) > 30 * 86400000) continue;
    const retired = x.status === 'revoked' && x.retired;
    alerts.push({ id: `xsubdecided:${x.expertTenantId || ''}:${x.expertNetworkId}:${x.decidedAt}`, kind: 'subscription-decided',
      fyi: true,
      title: x.status === 'active' ? `${x.name || x.expertNetworkId} approved your workspace`
        : retired ? `${x.name || x.expertNetworkId} was retired`
          : `${x.name || x.expertNetworkId} removed your workspace's access`,
      detail: x.status === 'active' ? 'Your rooms can plug it in and ask it now.' : 'Rooms that used it no longer reach it.',
      cta: 'Got it' });
  }

  for (const p of promotions || []) {
    if (!p || !p.requestId || (me && p.by === me)) continue;
    action.push({ id: `promotion:${p.requestId}`, kind: 'promotion',
      title: `Approve release v${p.version} of ${p.expertName || p.expertNetworkId} for Production`,
      detail: `${p.byName || 'A teammate'} asked${p.reason ? ` — ${p.reason}` : ''}. The rest of the company will be answered by it.`,
      to: `/network/${encodeURIComponent(p.expertNetworkId)}?tab=settings`, cta: 'Review' });
  }

  // ── ROOM LIFECYCLE (LIFECYCLE.md §5) — from the rooms the person is in (GET /my/agentic-networks). A room going
  //    quiet is THEIR call (use it, or let it go) → an ACTION item; any message or run in it keeps it (the warning
  //    leaves by itself). The workspace OWNER (Overwatch) also hears when a room was retired / archived (FYI). Each
  //    key carries its date, so the NEXT cycle is a new, unread item. ──
  const DAY = 86400000;
  const roomLink = (r) => `/agentic?networkId=${encodeURIComponent(r.networkId)}&poolId=${encodeURIComponent(r.poolId)}`;
  const nameOf = (r) => r.displayName || r.poolId || 'A room';
  for (const r of rooms || []) {
    if (!r || !r.networkId || !r.poolId) continue;
    const st = String(r.lifecycleStatus || '').toUpperCase();
    const id = `${r.networkId}:${r.poolId}`;
    // EXPERT ONBOARDING (EXPERT-NETWORKS.md §7) — an Expert Network room whose setup isn't finished: one ACTION item
    // that opens the room's "Get started" steps (the server flags it; finishing the steps removes it)
    if (r.onboardingPending) {
      action.push({ id: `room-setup:${id}`, kind: 'room-setup', title: `Finish setting up ${nameOf(r)}`,
        detail: 'A few plain steps so its experts know your setup.', to: `${roomLink(r)}&tab=setup`, cta: 'Continue' });
    }
    // EXPERT REQUESTS (EXPERT-ROOMS.md §6) — requests waiting for this expert team room's approval: one ACTION item
    if (Number(r.expertQueuePending) > 0) {
      const n = Number(r.expertQueuePending);
      action.push({ id: `room-requests:${id}`, key: `room-requests:${id}#${n}`, kind: 'room-requests',
        title: `${n} request${n === 1 ? '' : 's'} waiting in ${nameOf(r)}`,
        detail: 'Another room asked this team for something — approve or reject it.', to: `${roomLink(r)}&tab=requests`,
        cta: 'Review' });
    }
    // RULE CHANGES (EXPERT-NETWORKS.md §12) — a department loosened a rule: it waits for this Lead room's approval
    if (Number(r.ruleChangesPending) > 0) {
      const n = Number(r.ruleChangesPending);
      action.push({ id: `room-rule-changes:${id}`, key: `room-rule-changes:${id}#${n}`, kind: 'room-rule-changes',
        title: `${n} rule change${n === 1 ? '' : 's'} to approve in ${nameOf(r)}`,
        detail: 'A team asked to loosen one of its rules — approve or decline it.', to: `${roomLink(r)}&tab=rules`,
        cta: 'Review' });
    }
    if (st === 'NOTIFIED') {
      const w = r.inactivityWarning || {};
      const days = Number.isFinite(Number(w.daysUntilRetire)) ? Number(w.daysUntilRetire) : null;
      action.push({ id: `room-retire:${id}`, key: `room-retire:${id}#${w.retireAt || w.since || ''}`, kind: 'room-retire',
        title: `${nameOf(r)} will retire ${days === null ? 'soon' : days <= 0 ? 'today' : `in ${days} day${days === 1 ? '' : 's'}`}`,
        detail: 'No activity lately — post or run anything in it to keep it.', to: roomLink(r), cta: 'Open room' });
    } else if (isOwner && (st === 'RETIRED' || st === 'ARCHIVED')) {
      const at = st === 'ARCHIVED' ? r.archivedAt : r.retiredAt;
      const t = Date.parse(at || '');
      if (!Number.isFinite(t) || now - t > 14 * DAY) continue;                       // news for 14 days, like the rest
      const auto = String(r.retireReason || '').startsWith('AUTO');
      action.push({ id: `room-${st.toLowerCase()}:${id}`, key: `room-${st.toLowerCase()}:${id}#${at}`, kind: 'room-lifecycle',
        fyi: true,
        title: st === 'ARCHIVED' ? `${nameOf(r)} moved to Archive` : `${nameOf(r)} was retired${auto ? ' (inactive)' : ''}`,
        detail: st === 'ARCHIVED' ? 'Archived rooms are kept — you can move it back any time.'
          : 'It’s hidden from members. Restore it from the network’s Lifecycle tab.',
        to: `/network/${encodeURIComponent(r.networkId)}?tab=lifecycle`, cta: 'Lifecycle' });
    }
  }

  // ── approvals ──
  for (const a of approvals || []) {
    if (!a) continue;
    const st = String(a.status || '').toUpperCase();
    const room = `/agentic?networkId=${encodeURIComponent(a.networkId || '')}&poolId=${encodeURIComponent(a.poolId || '')}`;
    if (me && a.delegatedTo === me && st !== 'RESOLVED') {
      action.push({ id: `approval:${a.networkId}:${a.poolId}:${a.taskId}`, kind: 'approval',
        title: 'An approval was handed to you', detail: `${a.networkId} · ${a.poolId}`, to: room, cta: 'Open room' });
    } else if (isAdmin && st === 'ESCALATED') {
      overwatch.push({ id: `escalated:${a.networkId}:${a.poolId}:${a.taskId}`, kind: 'approval',
        title: 'Escalated approval', detail: `${a.networkId} · ${a.poolId}`, to: '/admin/approvals', cta: 'Review' });
    }
  }

  // ── alarms ──
  if (isAdmin) {
    for (const a of (alarms || []).filter(tripped)) {
      // ``key`` = id + trip number (INBOX.md §6): a RE-trip is a new, unread item even after you read / archived the last
      alerts.push({ id: `alarm:${a.Name}`, key: `alarm:${a.Name}#t${a.TripCount || 0}`, kind: 'alarm', critical: true, title: `Alarm tripped: ${a.Name}`,
        detail: String((a.LastError && a.LastError.detail) || '').slice(0, 140), to: '/workspace/settings?tab=alarms',
        cta: 'Open' });
    }
    for (const a of (roomAlarms || []).filter(tripped)) {
      const net = a.AlarmNetworkId || a.NetworkId || '';
      alerts.push({ id: `room-alarm:${net}:${a.Anchor || ''}:${a.Name || ''}`,
        key: `room-alarm:${net}:${a.Anchor || ''}:${a.Name || ''}#t${a.TripCount || 0}`, kind: 'room-alarm', critical: true,
        title: `${String(a.Anchor || '').startsWith('__') ? 'Build check' : 'Room alarm'}: ${a.Name || 'check'}`,
        detail: [net, String(a.Anchor || '').startsWith('__') ? '' : a.Anchor].filter(Boolean).join(' · '),
        // a BUILD-time alarm is anchored to the build's pseudo-room (``__build__``) — no room exists there, so it opens
        // the network's Build tab (the step that tripped); a real room's alarm opens the room (L175)
        to: !net ? '/workspace/settings?tab=alarms'
          : String(a.Anchor || '').startsWith('__') ? `/network/${encodeURIComponent(net)}`
            : `/agentic?networkId=${encodeURIComponent(net)}&poolId=${encodeURIComponent(a.Anchor || '')}`,
        cta: 'Open' });
    }
  }

  const count = [...action, ...alerts, ...overwatch].filter((x) => !x.fyi).length;   // FYI never inflates the badge
  const severity = alerts.some((x) => x.critical) ? 'critical' : count ? 'warn' : 'none';   // FYI alone stays quiet
  return { segments: { action, alerts, overwatch }, count, severity };
}

/** Which segments a person sees (owners/admins: all three). */
export function visibleSegments(isAdmin) {
  return isAdmin ? SEGMENTS : SEGMENTS.filter((s) => s.id === 'action');
}

/** Invites the person already ACCEPTED/DECLINED this session never come back from a slightly stale list read
 *  (the pending-invite index is eventually consistent — L143). PURE. */
export function withoutResolvedInvites(list, resolvedTokens) {
  const done = resolvedTokens instanceof Set ? resolvedTokens : new Set(resolvedTokens || []);
  return (list || []).filter((p) => p && !done.has(String(p.token || '').trim()));
}

